/*
 * ===============================================================================================================
 *                                Copyright 2025, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { ReactNode } from 'react';
import {
  useForm,
  Controller,
  ControllerFieldState,
  ControllerProps,
  ControllerRenderProps,
  FieldValues,
  UseFormTrigger,
  UseFormWatch
} from 'react-hook-form';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Switch,
  Typography
} from '@mui/material';
import { X, AlertCircle } from '@jda/lui-common-icon-library-mui5';
import { ErrorMessage } from '../../../shared/ErrorMessage/ErrorMessage';
import Loader from '../../../shared/Loader';
import { useStyles } from './styles';
import { ALLOW_ACCESS, DISALLOW_ACCESS, CLOSE, CANCEL_BTN_TEXT, SAVE } from '../constants';

export interface SettingsField {
  name: string;
  value: any;
  label: string;
  displayValue?: ReactNode;
  isEditAllowed?: (props: { watch: UseFormWatch<FieldValues> }) => boolean;
  controllerProps?: Partial<ControllerProps>;
  renderField?: (props: {
    field: ControllerRenderProps;
    fieldState: ControllerFieldState;
    trigger: UseFormTrigger<FieldValues>;
  }) => ReactNode;
  renderAfterField?: (props: { field: ControllerRenderProps }) => ReactNode;
}

interface SettingsFormDialogProps {
  title: string;
  fields: SettingsField[];
  onSubmit: (formValues: Record<string, any>) => Promise<void>;
  onClose: () => void;
  showAlert?: boolean;
  alertMessage?: string;
  sizeDialog?: any;
}

const SettingsFormDialog = ({ title, fields, onSubmit, onClose, showAlert, alertMessage, sizeDialog }: SettingsFormDialogProps) => {
  const {
    formState: { isDirty, isSubmitting, isValid, errors },
    watch,
    handleSubmit,
    setError,
    trigger,
    control
  } = useForm({ mode: 'onChange' });

  const { classes } = useStyles();

  const handleSave = handleSubmit(async formValues => {
    try {
      await onSubmit(formValues);
      onClose();
    } catch (error: any) {
      setError('root.serverError', { message: error.message });
    }
  });

  return (
    <Dialog open={true} maxWidth={sizeDialog || 'sm'} fullWidth={true} aria-labelledby="settings-edit-dialog-title">
      <DialogTitle id="settings-edit-dialog-title" className={classes.dialogTitle}>
        <Typography variant="h4" className={classes.dialogTitleText}>
          {title}
        </Typography>
        <IconButton aria-label={CLOSE}  onClick={onClose}>
          <X />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers={false} className={classes.dialogContent}>
        {fields.map(field =>
          field.isEditAllowed && !field.isEditAllowed({ watch }) ? null : (
            <Controller
              name={field.name}
              control={control}
              render={({ field: formField, fieldState }) => (
                <Box className={classes.formField}>
                  <Grid container={true} sx={{ alignItems: 'center' }}>
                    <Grid item={true} xs={9}>
                      <Typography>{field.label}:
                      </Typography>
                    </Grid>
                    <Grid item={true} xs={3}>
                      {field.renderField ? (
                        field.renderField({ field: formField, fieldState, trigger })
                      ) : [true, false].includes(formField.value) ? (
                        <FormControlLabel
                          control={
                            <Switch
                              name={formField.name}
                              className={classes.toggleSwitch}
                              checked={formField.value}
                              onChange={formField.onChange}
                              color="primary"
                              disabled={formField.disabled}
                            />
                          }
                          label={formField.value ? ALLOW_ACCESS : DISALLOW_ACCESS}
                        />
                      ) : null}
                    </Grid>
                  </Grid>
                  {fieldState.error && <FormHelperText error={true}>{fieldState.error.message}</FormHelperText>}
                  {field.renderAfterField && field.renderAfterField({ field: formField })}
                </Box>
              )}
              defaultValue={field.value}
              key={field.name}
              {...field.controllerProps}
            />
          )
        )}
        {errors.root?.serverError?.message && <ErrorMessage message={errors.root.serverError.message} />}
      </DialogContent>
      <DialogActions className={showAlert ? classes.dialogActionsWithAlert : classes.dialogActions}>
        {showAlert && <Box className={classes.dialogNote}>
          <AlertCircle fontSize='small' className={classes.dialogNoteIcon} />
          <Typography variant="body2" color="textSecondary" style={{ marginLeft: 8}}>
            {alertMessage}
          </Typography>
        </Box>}
        <Box className={classes.actionsBox}>
          {isSubmitting && <Loader showLoader={true} />}
          <Button variant="outlined" color="primary" onClick={onClose}>
            {CANCEL_BTN_TEXT}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!isDirty || isSubmitting || !isValid}
            onClick={handleSave}
            style={{ marginLeft: 8 }}
          >
            {SAVE}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsFormDialog;
